import { FormErrors } from "@mantine/form/lib/types";
import { TFunction } from "i18next";
import { CampaignInquiryProductInput, Client, QuestionInput, QuestionType } from "src/graphql/generated/graphql";
import { DeliveryState } from "src/hooks/campaigns";
import { DeepPartial } from "src/types/utils";

import type { CreateCampaignInquiryFormValues } from "./formContext";

const mapProduct = (product: DeepPartial<CampaignInquiryProductInput>) => ({
    ...product,
    category: {
        key: product.category?.key || "",
    },
    attributes: product.attributes
        ? product.attributes.map((attr) => ({ id: attr.id || "", locale: attr.locale || "", name: attr.name || "" }))
        : [],
    destinations: product.destinations
        ? product.destinations.map((dest) => ({
              // Spread first: enumerating the fields here silently dropped isOrderNumber, so
              // Datart campaigns never asked testers for an order number (TTV2-1623). `config`
              // stays `?? undefined` — the tenant catalog omits it, and "" is not "unset".
              ...dest,
              key: dest.key || "",
              name: dest.name || "",
              type: dest.type || "text",
              config: dest.config ?? undefined,
              configFields: dest.configFields
                  ? dest.configFields.map((field) => ({
                        label: field.label || "",
                        path: field.path || "",
                        type: field.type || "text",
                        placeholder: field.placeholder || "",
                        isRequiredForApproval: field.isRequiredForApproval || false,
                        urlStripQueryString: field.urlStripQueryString || false,
                    }))
                  : [],
          }))
        : [],
});

export type PartialCreateCampaignInquiryInput = DeepPartial<CreateCampaignInquiryFormValues>;

/** What openAt can actually hold by the time it is read back.
 *
 * The form types it as a number, but it does not always arrive as one. Mantine's
 * DatePickerInput puts a Date into the form values, and a draft round-tripped through
 * localStorage comes back as an ISO string — readCampaignInquiryDraft asserts the parsed
 * JSON straight onto the draft type, so TypeScript never sees the difference.
 */
export type OpenAtValue = number | string | Date | null;

/** openAt as a timestamp, or null when it is absent or unparseable.
 *
 * Normalising before comparing is load-bearing: a bare `openAt < startOfToday` against a
 * string coerces to NaN, which is never true, so the guard below used to pass every
 * resumed draft silently — the one case its own comment promised to cover.
 */
export const toOpenAtTimestamp = (openAt?: OpenAtValue): number | null => {
    if (openAt == null) {
        return null;
    }

    const timestamp = typeof openAt === "number" ? openAt : new Date(openAt).getTime();

    return Number.isNaN(timestamp) ? null : timestamp;
};

/** A4: "in the past" means before the start of today, in the browser's local timezone —
 * a date picked earlier today is not in the past. Shared by getFormValues (R13, a
 * duplicate must not carry a stale openAt forward) and the inquiry form's own validate
 * (R14, a client cannot pick a past date either), so the boundary can never disagree
 * between the two.
 */
export const isOpenAtInPast = (openAt?: OpenAtValue): boolean => {
    const timestamp = toOpenAtTimestamp(openAt);

    if (timestamp == null) {
        return false;
    }

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    return timestamp < startOfToday;
};

/** The wizard's three tabs, in the order they appear down the left rail. */
export type CampaignInquirySection = "settings" | "products" | "questionnaire";

const CAMPAIGN_INQUIRY_SECTION_ORDER: CampaignInquirySection[] = ["settings", "products", "questionnaire"];

/** Translation key naming each tab in a refusal notification.
 *
 * Deliberately the wizard's own tab titles: the section the client is told about and
 * the tab they are sent to then always read the same words.
 */
export const campaignInquirySectionLabelKeys: Record<CampaignInquirySection, string> = {
    settings: "campaigninquiry_create_tab_settings",
    products: "campaigninquiry_create_tab_products",
    questionnaire: "campaigninquiry_create_tab_questionare",
};

/** Which tab owns an error key.
 *
 * Mirrors the tabs' own hasError predicates: `products.` and `questionnaire.` claim
 * their tabs, and the settings tab owns everything else by exclusion — so a new
 * top-level rule marks the right tab without an edit here.
 */
export const campaignInquirySectionOfErrorKey = (key: string): CampaignInquirySection => {
    if (/^products\./.test(key)) {
        return "products";
    }

    if (/^questionnaire\./.test(key)) {
        return "questionnaire";
    }

    return "settings";
};

/** The tabs refusing the submit, in tab order. Empty ⇒ nothing refuses it.
 *
 * Entries with a null/empty message are ignored: Mantine leaves a cleared rule in the
 * errors object as null, and a null is not a refusal.
 */
export const getFailedCampaignInquirySections = (errors?: FormErrors | null): CampaignInquirySection[] => {
    const failed = new Set<CampaignInquirySection>();

    for (const [key, message] of Object.entries(errors || {})) {
        if (message) {
            failed.add(campaignInquirySectionOfErrorKey(key));
        }
    }

    return CAMPAIGN_INQUIRY_SECTION_ORDER.filter((section) => failed.has(section));
};

/** The tab index a section sits at, so a refused submit can send the client to the
 * first tab that actually refused it. */
export const campaignInquirySectionTabIndex = (section: CampaignInquirySection): number =>
    CAMPAIGN_INQUIRY_SECTION_ORDER.indexOf(section);

/** R13: the openAt a form should start from — always a timestamp, never in the past.
 * A duplicate must not carry a stale date forward, and neither must a resumed draft.
 */
export const healOpenAt = (openAt?: OpenAtValue): number => {
    const timestamp = toOpenAtTimestamp(openAt);

    return timestamp != null && !isOpenAtInPast(timestamp) ? timestamp : new Date().getTime();
};

/**
 * Does a questionnaire question carry anything the client actually wrote? A question
 * with no text and no option carrying non-whitespace text is not a question — it is
 * what the editor auto-seeds on mount (QuestionsEditorForm/Question) and what a client
 * who never opens the Dotazník tab submits unwittingly. Shared by the inquiry form's
 * validate (R10, such a question is not validated) and its transformValues (R12, such a
 * question is not submitted) so the two can never disagree about what "untouched" means.
 */
export const hasAuthoredQuestionContent = (question?: DeepPartial<QuestionInput>): boolean =>
    !!question?.text?.trim() || (question?.options || []).some((option) => !!option?.text?.trim());

/**
 * Only radio and checkbox render options (QuestionnaireEditor/questionsForm/question.tsx's
 * own questionTypesWithOptions) — a copied question of any other type may still carry
 * leftover options, but the client cannot fix a field the editor never renders either.
 * One home for this rule: reused by both getQuestionnaireQuestionErrors below (the inquiry
 * form's own validate, R10, AND the nested QuestionnaireEditor form's validate, R10b) so
 * neither can drift into a type-blind check that resurrects the invisible-error bug this
 * ticket exists to remove.
 */
const QUESTION_TYPES_WITH_OPTIONS: QuestionType[] = [QuestionType.Radio, QuestionType.Checkbox];

export const questionTypeHasOptions = (type?: QuestionType | string | null): boolean =>
    QUESTION_TYPES_WITH_OPTIONS.includes(type as QuestionType);

/**
 * R10/R10b: the questionnaire problems a client must fix before creating the inquiry —
 * empty question text (any type), empty option text on radio/checkbox, and a condition
 * missing its dependsOn/type — reported under `keyPrefix`.
 *
 * One function for both places that report these: the inquiry form
 * (keyPrefix "questionnaire.questions", this form's own values tree — the only form whose
 * validate is actually wired up to refuse the submit) and the nested QuestionnaireEditor
 * form (keyPrefix "questions", the fields QuestionsEditorForm actually renders — marks the
 * offending field itself, test-spec.md's R10b). Keeping the logic in one place is what
 * stops the two from ever disagreeing about which question/option is broken.
 *
 * A question with no authored content at all (see hasAuthoredQuestionContent) is skipped
 * entirely — that is what the wizard auto-seeds on mount, and validating it would make the
 * wizard impossible to submit without ever opening the Dotazník tab (test-spec.md §3.2).
 */
export const getQuestionnaireQuestionErrors = (
    questions: DeepPartial<QuestionInput>[] | undefined,
    t: TFunction,
    keyPrefix: string
): FormErrors =>
    (questions || []).reduce<FormErrors>((errors, question, questionIndex) => {
        if (!hasAuthoredQuestionContent(question)) {
            return errors;
        }

        const prefix = `${keyPrefix}.${questionIndex}`;

        return {
            ...errors,
            [`${prefix}.text`]: !(question?.text?.trim()?.length > 0)
                ? t("questionnaire_error_missingquestioncontent")
                : null,
            ...(question?.condition
                ? {
                      [`${prefix}.condition.dependsOn`]: !(question.condition.dependsOn?.length > 0)
                          ? t("questionnaire_error_missingquestiondependson")
                          : null,
                      [`${prefix}.condition.type`]: !(question.condition.type?.length > 0)
                          ? t("questionnaire_error_missingquestiondependsontype")
                          : null,
                  }
                : {}),
            ...(questionTypeHasOptions(question?.type)
                ? (question?.options || []).reduce<FormErrors>(
                      (optionErrors, option, optionIndex) => ({
                          ...optionErrors,
                          [`${prefix}.options.${optionIndex}.text`]: !(option?.text?.trim()?.length > 0)
                              ? t("questionnaire_error_missingquestionoptionvalue")
                              : null,
                      }),
                      {}
                  )
                : {}),
        };
    }, {});

/**
 * Does the inquiry form carry a questionnaire error? Mirrors how the products tab
 * already reads its own errors: a key under the questionnaire field with a non-empty
 * message. This is what the Dotazník tab's hasError must return.
 */
export const hasQuestionnaireError = (errors?: FormErrors): boolean =>
    Object.keys(errors || {}).some((key) => key.startsWith("questionnaire.") && !!errors?.[key]);

export const getFormValues = (values?: PartialCreateCampaignInquiryInput): PartialCreateCampaignInquiryInput => ({
    name: values?.name ?? "",
    clientId: values?.clientId ?? "",
    tenantId: values?.tenantId ?? null,
    displayTenantIds: values?.displayTenantIds ?? [],
    mainTenantId: values?.mainTenantId ?? null,
    products: values?.products ? values.products.map(mapProduct) : [],
    description: values?.description ?? null,
    settings: {
        capacity: values?.settings?.capacity ?? 0,
        openAt: healOpenAt(values?.settings?.openAt),
    },
    restrictions: values?.restrictions ?? null,
    pricing: {
        isFree: values?.pricing?.isFree ?? true,
        discountPercent: values?.pricing?.discountPercent ?? 100,
        discountCodes: values?.pricing?.discountCodes ?? [],
    },
    delivery: {
        freeOfCharge: values?.delivery?.freeOfCharge ?? false,
        type: values?.delivery?.type ?? DeliveryState.PersonalPickup,
        notes: values?.delivery?.notes ?? "",
    },
    questionnaire: values?.questionnaire ?? null,
    thumbnailImage: values?.thumbnailImage ?? null,
    images: values?.images ?? [],
    additionalNotes: values?.additionalNotes ?? "",
});

/**
 * A new inquiry always starts from a clean sheet. Restoring a saved draft is a
 * separate, explicit act — see readCampaignInquiryDraft below.
 */
export const getDefaultValues = (client: Client): PartialCreateCampaignInquiryInput => ({
    ...getFormValues(),
    clientId: client?.id,
});

/*
 * Draft persistence (TTV2-1569).
 *
 * A draft is scoped to the client AND to the intent it was written under, and
 * carries its schema version plus the time it was saved. All of that is checked
 * when the draft is READ, so a draft belonging to another intent, to an older
 * form shape, or to a session abandoned days ago simply cannot be claimed. That
 * matters because the alternative — clearing the draft when the form unmounts —
 * cannot carry the guarantee: a reload or a closed tab runs no teardown, and
 * making cleanup responsible for correctness would also throw away the draft in
 * exactly the crash cases it exists for.
 *
 * Reading a draft never applies it. The caller offers it to the client.
 */
export const CAMPAIGN_INQUIRY_DRAFT_VERSION = 2;

const DRAFT_KEY_PREFIX = "createCampaignInquiryForm";
/** Pre-TTV2-1569 drafts lived in a single unscoped, unstamped slot per browser. */
const LEGACY_DRAFT_KEY = DRAFT_KEY_PREFIX;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export type CampaignInquiryDraftIntent = "new" | `copy:${string}`;

interface CampaignInquiryDraft {
    version: number;
    savedAt: number;
    clientId: string;
    intent: CampaignInquiryDraftIntent;
    values: PartialCreateCampaignInquiryInput;
}

const draftKey = (clientId: string, intent: CampaignInquiryDraftIntent) => `${DRAFT_KEY_PREFIX}:${clientId}:${intent}`;

// Safari with cookies/site-data blocked throws on localStorage instead of
// returning null, and TTV2-1569 was reported on Safari — so guard every access.
const readStorage = (key: string) => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage unavailable. The form still works, it just won't survive a reload.
    }
};

const removeStorage = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {
        // Nothing to do.
    }
};

export const clearCampaignInquiryDraft = (clientId: string, intent: CampaignInquiryDraftIntent) => {
    if (clientId) {
        removeStorage(draftKey(clientId, intent));
    }
    removeStorage(LEGACY_DRAFT_KEY);
};

export const writeCampaignInquiryDraft = (
    clientId: string,
    intent: CampaignInquiryDraftIntent,
    values: PartialCreateCampaignInquiryInput
) => {
    if (!clientId) {
        return;
    }

    const draft: CampaignInquiryDraft = {
        version: CAMPAIGN_INQUIRY_DRAFT_VERSION,
        savedAt: new Date().getTime(),
        clientId,
        intent,
        values,
    };

    writeStorage(draftKey(clientId, intent), JSON.stringify(draft));
};

export const readCampaignInquiryDraft = (
    clientId: string,
    intent: CampaignInquiryDraftIntent
): PartialCreateCampaignInquiryInput | null => {
    // A draft written before TTV2-1569 is unscoped and unstamped — it is exactly
    // the state that used to reappear behind "create a new inquiry" — so drop it
    // rather than trying to interpret it.
    removeStorage(LEGACY_DRAFT_KEY);

    if (!clientId) {
        return null;
    }

    const key = draftKey(clientId, intent);
    const raw = readStorage(key);

    if (!raw) {
        return null;
    }

    let draft: CampaignInquiryDraft;
    try {
        draft = JSON.parse(raw);
    } catch {
        removeStorage(key);
        return null;
    }

    const isClaimable =
        draft?.version === CAMPAIGN_INQUIRY_DRAFT_VERSION &&
        draft?.clientId === clientId &&
        draft?.intent === intent &&
        typeof draft?.savedAt === "number" &&
        new Date().getTime() - draft.savedAt < DRAFT_TTL_MS &&
        !!draft?.values;

    if (!isClaimable) {
        removeStorage(key);
        return null;
    }

    return draft.values;
};

/*
 * TTV2-1659: the questionnaire question limit.
 *
 * The server refuses a questionnaire over the client's `maxQuestionnaireQuestions` (default 5
 * when the client carries none) — these helpers let the form say so before the request is sent.
 */
export const QUESTIONNAIRE_QUESTIONS_LIMIT_DEFAULT = 5;

/** `??`, not `||`: a client configured with a limit of 0 has to see 0. */
export const getQuestionnaireLimit = (client?: Pick<Client, "maxQuestionnaireQuestions"> | null): number =>
    client?.maxQuestionnaireQuestions ?? QUESTIONNAIRE_QUESTIONS_LIMIT_DEFAULT;

/** How many questions will actually be submitted — the authored ones, never the auto-seeded placeholders. */
export const countAuthoredQuestions = (questions?: DeepPartial<QuestionInput>[] | null): number =>
    (questions || []).filter((question) => hasAuthoredQuestionContent(question)).length;

/** The numbers behind the over-limit message, or null when the questionnaire is within its limit. */
export const getQuestionnaireOverage = (
    questions: DeepPartial<QuestionInput>[] | undefined | null,
    limit: number
): { total: number; limit: number; over: number } | null => {
    const total = countAuthoredQuestions(questions);

    return total > limit ? { total, limit, over: total - limit } : null;
};
